import { apiIntegrations } from "@/api/integrations/integration.api.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  integrationEmployeesFilterSource,
  IntegrationEmployeesFilter,
  toApiIntegrationEmployeesFilter,
  useIntegrationEmployeesFilters,
} from "@/pages/settings/integrations/components/integration-employees.filters.ts";
import { useIntegrationEmployeesSync } from "@/queries/integrations/use-integrations.query.ts";
import { ApiIntegrationEmployeesType } from "@/types/integrations/integration.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  useToasts,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface Props {
  integrationId: number;
  integrationType: ApiIntegrationEmployeesType;
  isActive: boolean;
  isEmployeesTab: boolean;
}

export function SettingsIntegrationEmployeesActions({
  integrationId,
  integrationType,
  isActive,
  isEmployeesTab,
}: Props) {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { canIEdit } = usePermissions();
  const sync = useIntegrationEmployeesSync();
  const { isExporting, downloadFile } = useDownloadHelper();
  const {
    filterButton,
    filter: { filter },
  } = useAppFilter<IntegrationEmployeesFilter>({
    filters: useIntegrationEmployeesFilters(),
    source: integrationEmployeesFilterSource,
  });

  const handleSync = () => {
    sync.mutate(
      { id: integrationId, integrationType },
      { onSuccess: () => showInfo({ title: t("integrations:sync_started") }) }
    );
  };

  const handleExport = () => {
    const { status, text } = toApiIntegrationEmployeesFilter(filter);
    downloadFile({
      apiCall: () =>
        apiIntegrations.exportEmployees(integrationId, integrationType, { status, text }),
    });
  };

  return (
    <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
      {isActive && (
        <Button variant={"secondary"} isLoading={sync.isPending} onClick={handleSync}>
          {t("integrations:sync_now")}
        </Button>
      )}
      {isEmployeesTab && canIEdit(ApiPermission.reporting_export) && (
        <DropdownMenu
          trigger={
            <Button isLoading={isExporting} variant={"secondary"}>
              {`${t("common:export")}...`}
            </Button>
          }
        >
          <DropdownMenuItem
            onClick={handleExport}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
            title={t("common:export_to_xlsx")}
          />
        </DropdownMenu>
      )}
      {isEmployeesTab && filterButton()}
    </Stack>
  );
}
